/**
 * Shared procedural weapon model builder.
 *
 * Extracted from LocalWeaponView's `rebuild` method so the in-hand
 * view-model and the dropped item on the ground render with the EXACT
 * same procedural parts — same materials, same positions, same
 * silhouette. The only difference is whether animation refs (slide,
 * magazine) are captured: in-hand needs them for reload / slide-pull
 * animations; the ground item doesn't care.
 *
 * Callers:
 *   • LocalWeaponView — passes a `refs` object so it can grab the
 *     animation entities after building.
 *   • weaponWorldModel.buildWeaponWorldModel — no refs, just the mesh.
 *
 * The `addPart` + `mat` helpers in here are the canonical pair; the
 * previous duplicate in `weaponWorldModel.ts` (which used
 * `meshInstances[]` iteration instead of `render.material = …`) was the
 * root cause of dropped weapons rendering inconsistently — that whole
 * file now wraps this builder.
 */

import * as pc from "playcanvas";
import { getWeapon, type WeaponClass } from "@hupper/shared/weapons";
import { buildMolotov } from "./molotovDesign";

/** Optional refs the caller can collect after build (used by the in-hand
 *  view-model for slide-pull / mag-drop animation hooks). The ground
 *  item caller passes nothing. */
export interface WeaponBuildRefs {
  slideEntity?: pc.Entity | null;
  magEntity?: pc.Entity | null;
  slideOriginalPos?: pc.Vec3;
  magOriginalPos?: pc.Vec3;
}

export function mat(r: number, g: number, b: number, gloss = 0.35): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(r, g, b);
  m.gloss = gloss;
  m.update();
  return m;
}

export function addPart(
  parent: pc.Entity,
  type: "box" | "cylinder" | "capsule" | "sphere",
  name: string,
  scale: [number, number, number],
  pos: [number, number, number],
  euler: [number, number, number],
  material: pc.StandardMaterial,
): pc.Entity {
  const e = new pc.Entity(name);
  e.addComponent("render", { type });
  e.setLocalScale(scale[0], scale[1], scale[2]);
  e.setLocalPosition(pos[0], pos[1], pos[2]);
  e.setLocalEulerAngles(euler[0], euler[1], euler[2]);
  // Same pattern grenadeViewModel uses — `render.material = …` is the
  // single-shot setter that propagates to every mesh instance, whether
  // they're materialized synchronously or not. The earlier `meshInstances`
  // iteration path silently no-op'd when the array wasn't populated yet.
  const render = e.render;
  if (render) {
    render.material = material;
    render.castShadows = false;
    render.receiveShadows = false;
  }
  parent.addChild(e);
  return e;
}

export function accentForClass(cls: WeaponClass): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  switch (cls) {
    case "pistol":
      m.diffuse = new pc.Color(0.55, 0.2, 0.15);
      break;
    case "smg":
      m.diffuse = new pc.Color(0.85, 0.55, 0.12);
      break;
    case "rifle":
      m.diffuse = new pc.Color(0.2, 0.45, 0.28);
      break;
    case "sniper":
      m.diffuse = new pc.Color(0.15, 0.22, 0.55);
      break;
    default:
      m.diffuse = new pc.Color(0.4, 0.4, 0.42);
  }
  m.gloss = 0.45;
  m.update();
  return m;
}

/**
 * Build a weapon model into `parent`. Optionally populate `refs` with
 * the slide + magazine entities (and their original local positions)
 * so the caller can animate them. This is a verbatim port of the
 * mesh-build half of LocalWeaponView.rebuild — both call sites get
 * pixel-identical models.
 */
export function buildWeaponModel(
  parent: pc.Entity,
  weaponId: string,
  refs?: WeaponBuildRefs,
): void {
  let w;
  try {
    w = getWeapon(weaponId);
  } catch {
    w = getWeapon("g18");
  }

  const steel = mat(0.38, 0.39, 0.42, 0.55);
  const dark = mat(0.12, 0.12, 0.13, 0.25);
  const poly = mat(0.16, 0.16, 0.18, 0.2);
  const accent = accentForClass(w.class);

  // ── Local helpers that route into the optional refs object ─────────
  const setSlide = (e: pc.Entity, x: number, y: number, z: number) => {
    if (!refs) return;
    refs.slideEntity = e;
    refs.slideOriginalPos = new pc.Vec3(x, y, z);
  };
  const setMag = (e: pc.Entity, x: number, y: number, z: number) => {
    if (!refs) return;
    refs.magEntity = e;
    refs.magOriginalPos = new pc.Vec3(x, y, z);
  };

  switch (w.class) {
    case "melee":
      // Handle
      addPart(parent, "box", "knife-handle", [0.025, 0.12, 0.035], [0, -0.02, 0], [0, 0, 0], poly);
      // Grip texture (ridges)
      addPart(parent, "box", "knife-grip-1", [0.028, 0.01, 0.04], [0, -0.04, 0], [0, 0, 0], dark);
      addPart(parent, "box", "knife-grip-2", [0.028, 0.01, 0.04], [0, -0.01, 0], [0, 0, 0], dark);
      addPart(parent, "box", "knife-grip-3", [0.028, 0.01, 0.04], [0, 0.02, 0], [0, 0, 0], dark);
      // Pommel Ring (Karambit style)
      addPart(parent, "cylinder", "knife-pommel-ring", [0.04, 0.01, 0.04], [0, -0.09, 0], [90, 0, 0], steel);
      addPart(parent, "cylinder", "knife-pommel-hole", [0.03, 0.012, 0.03], [0, -0.09, 0], [90, 0, 0], dark);
      // Guard
      addPart(parent, "box", "knife-guard", [0.04, 0.02, 0.05], [0, 0.04, 0], [0, 0, 0], steel);
      // Blade main
      addPart(parent, "box", "knife-blade-main", [0.01, 0.18, 0.035], [0, 0.14, 0], [0, 0, 0], steel);
      // Blade tip (angled)
      addPart(parent, "box", "knife-blade-tip", [0.01, 0.06, 0.035], [0, 0.25, -0.01], [20, 0, 0], steel);
      // Serrations on back
      addPart(parent, "box", "knife-serration-1", [0.012, 0.01, 0.01], [0, 0.08, 0.015], [0, 0, 0], dark);
      addPart(parent, "box", "knife-serration-2", [0.012, 0.01, 0.01], [0, 0.10, 0.015], [0, 0, 0], dark);
      addPart(parent, "box", "knife-serration-3", [0.012, 0.01, 0.01], [0, 0.12, 0.015], [0, 0, 0], dark);
      addPart(parent, "box", "knife-serration-4", [0.012, 0.01, 0.01], [0, 0.14, 0.015], [0, 0, 0], dark);
      // Blood groove
      addPart(parent, "box", "knife-groove", [0.012, 0.12, 0.005], [0, 0.14, 0], [0, 0, 0], dark);
      break;

    case "pistol":
      if (weaponId === "usp_s") {
        // USP-S (Suppressed)
        addPart(parent, "box", "pistol-grip", [0.035, 0.11, 0.05], [0, -0.06, 0], [12, 0, 0], poly);
        addPart(parent, "box", "pistol-frame", [0.04, 0.03, 0.18], [0, 0, -0.06], [0, 0, 0], dark);
        {
          const slide = addPart(parent, "box", "pistol-slide", [0.042, 0.04, 0.18], [0, 0.03, -0.06], [0, 0, 0], steel);
          setSlide(slide, 0, 0.03, -0.06);
        }
        addPart(parent, "cylinder", "pistol-suppressor", [0.035, 0.18, 0.035], [0, 0.025, -0.24], [90, 0, 0], dark);
        addPart(parent, "box", "pistol-guard-bottom", [0.01, 0.01, 0.05], [0, -0.03, -0.05], [0, 0, 0], poly);
        addPart(parent, "box", "pistol-guard-front", [0.01, 0.04, 0.01], [0, -0.01, -0.07], [0, 0, 0], poly);
        addPart(parent, "box", "pistol-sight-rear", [0.02, 0.01, 0.01], [0, 0.055, 0.02], [0, 0, 0], dark);
        addPart(parent, "box", "pistol-sight-front", [0.01, 0.01, 0.01], [0, 0.055, -0.14], [0, 0, 0], accent);
        {
          const m = addPart(parent, "box", "pistol-mag", [0.03, 0.1, 0.04], [0, -0.1, 0], [12, 0, 0], dark);
          setMag(m, 0, -0.1, 0);
        }
      } else if (weaponId === "deagle") {
        // Desert Eagle (Heavy, metallic)
        addPart(parent, "box", "deagle-grip", [0.04, 0.12, 0.055], [0, -0.06, 0], [10, 0, 0], poly);
        addPart(parent, "box", "deagle-frame", [0.045, 0.035, 0.22], [0, 0, -0.07], [0, 0, 0], steel);
        {
          const slide = addPart(parent, "box", "deagle-slide", [0.05, 0.05, 0.22], [0, 0.04, -0.07], [0, 0, 0], steel);
          setSlide(slide, 0, 0.04, -0.07);
        }
        addPart(parent, "cylinder", "deagle-barrel", [0.025, 0.23, 0.025], [0, 0.03, -0.07], [90, 0, 0], dark);
        addPart(parent, "box", "deagle-guard-bottom", [0.012, 0.012, 0.06], [0, -0.03, -0.06], [0, 0, 0], steel);
        addPart(parent, "box", "deagle-guard-front", [0.012, 0.05, 0.012], [0, -0.01, -0.09], [0, 0, 0], steel);
        addPart(parent, "box", "deagle-sight-rear", [0.025, 0.015, 0.015], [0, 0.07, 0.03], [0, 0, 0], dark);
        addPart(parent, "box", "deagle-sight-front", [0.015, 0.015, 0.015], [0, 0.07, -0.17], [0, 0, 0], accent);
        {
          const m = addPart(parent, "box", "deagle-mag", [0.035, 0.1, 0.045], [0, -0.1, 0], [10, 0, 0], dark);
          setMag(m, 0, -0.1, 0);
        }
      } else if (weaponId === "dualies") {
        // Dual Berettas (Render two pistols)
        const renderPistol = (offsetX: number) => {
          addPart(parent, "box", `dual-grip-${offsetX}`, [0.03, 0.10, 0.045], [offsetX, -0.06, 0], [12, 0, 0], poly);
          addPart(parent, "box", `dual-frame-${offsetX}`, [0.035, 0.025, 0.16], [offsetX, 0, -0.06], [0, 0, 0], dark);
          const slide = addPart(parent, "box", `dual-slide-${offsetX}`, [0.038, 0.035, 0.16], [offsetX, 0.03, -0.06], [0, 0, 0], steel);
          addPart(parent, "cylinder", `dual-barrel-${offsetX}`, [0.018, 0.17, 0.018], [offsetX, 0.025, -0.06], [90, 0, 0], dark);
          addPart(parent, "box", `dual-guard-bottom-${offsetX}`, [0.01, 0.01, 0.04], [offsetX, -0.03, -0.05], [0, 0, 0], poly);
          addPart(parent, "box", `dual-guard-front-${offsetX}`, [0.01, 0.03, 0.01], [offsetX, -0.01, -0.07], [0, 0, 0], poly);
          const m = addPart(parent, "box", `dual-mag-${offsetX}`, [0.025, 0.09, 0.035], [offsetX, -0.1, 0], [12, 0, 0], dark);
          if (offsetX > 0) {
            setSlide(slide, offsetX, 0.03, -0.06);
            setMag(m, offsetX, -0.1, 0);
          }
        };
        renderPistol(-0.15);
        renderPistol(0.15);
      } else {
        // Default G18
        addPart(parent, "box", "pistol-grip", [0.035, 0.11, 0.05], [0, -0.06, 0], [12, 0, 0], poly);
        addPart(parent, "box", "pistol-grip-panel-l", [0.005, 0.09, 0.04], [-0.018, -0.06, 0], [12, 0, 0], dark);
        addPart(parent, "box", "pistol-grip-panel-r", [0.005, 0.09, 0.04], [0.018, -0.06, 0], [12, 0, 0], dark);
        {
          const m = addPart(parent, "box", "pistol-mag-base", [0.038, 0.1, 0.055], [0, -0.12, 0.01], [12, 0, 0], dark);
          setMag(m, 0, -0.12, 0.01);
        }
        addPart(parent, "box", "pistol-frame", [0.04, 0.03, 0.18], [0, 0, -0.06], [0, 0, 0], dark);
        {
          const slide = addPart(parent, "box", "pistol-slide", [0.042, 0.04, 0.18], [0, 0.03, -0.06], [0, 0, 0], steel);
          setSlide(slide, 0, 0.03, -0.06);
        }
        addPart(parent, "box", "pistol-slide-serrations", [0.044, 0.03, 0.03], [0, 0.03, 0.01], [0, 0, 0], dark);
        addPart(parent, "box", "pistol-ejection-port", [0.044, 0.01, 0.03], [0.005, 0.04, -0.05], [0, 0, 0], dark);
        addPart(parent, "cylinder", "pistol-barrel", [0.02, 0.19, 0.02], [0, 0.025, -0.06], [90, 0, 0], dark);
        addPart(parent, "box", "pistol-guard-bottom", [0.01, 0.01, 0.05], [0, -0.03, -0.05], [0, 0, 0], poly);
        addPart(parent, "box", "pistol-guard-front", [0.01, 0.04, 0.01], [0, -0.01, -0.07], [0, 0, 0], poly);
        addPart(parent, "box", "pistol-trigger", [0.008, 0.02, 0.01], [0, -0.01, -0.04], [15, 0, 0], steel);
        addPart(parent, "box", "pistol-hammer", [0.01, 0.02, 0.015], [0, 0.04, 0.04], [-20, 0, 0], steel);
        addPart(parent, "box", "pistol-light-body", [0.025, 0.025, 0.06], [0, -0.02, -0.12], [0, 0, 0], dark);
        addPart(parent, "cylinder", "pistol-light-lens", [0.02, 0.01, 0.02], [0, -0.02, -0.15], [90, 0, 0], mat(0.8, 0.9, 1.0, 0.9));
        addPart(parent, "box", "pistol-sight-rear", [0.02, 0.01, 0.01], [0, 0.055, 0.02], [0, 0, 0], dark);
        addPart(parent, "box", "pistol-sight-front", [0.01, 0.01, 0.01], [0, 0.055, -0.14], [0, 0, 0], accent);
      }
      break;

    case "smg":
      if (weaponId === "mac10") {
        // MAC-10 - boxy, compact, vertical grip, wire stock, charging handle on top
        const receiverMat = mat(0.3, 0.3, 0.32, 0.4);
        const polyMat = mat(0.15, 0.15, 0.15, 0.2);
        
        // Main blocky receiver (lower and upper)
        addPart(parent, "box", "mac10-receiver-lower", [0.045, 0.08, 0.16], [0, -0.02, -0.05], [0, 0, 0], receiverMat);
        addPart(parent, "box", "mac10-receiver-upper", [0.045, 0.05, 0.16], [0, 0.045, -0.05], [0, 0, 0], receiverMat);
        
        // Barrel (short, stubby)
        addPart(parent, "cylinder", "mac10-barrel", [0.02, 0.06, 0.02], [0, 0.04, -0.15], [90, 0, 0], dark);
        
        // Canvas front strap / grip (iconic MAC-10 strap)
        addPart(parent, "box", "mac10-strap-base", [0.03, 0.01, 0.02], [0, -0.06, -0.12], [0, 0, 0], dark);
        addPart(parent, "box", "mac10-strap-loop", [0.035, 0.08, 0.005], [0, -0.1, -0.12], [10, 0, 0], dark);
        addPart(parent, "box", "mac10-strap-bottom", [0.035, 0.005, 0.03], [0, -0.14, -0.115], [0, 0, 0], dark);
        
        // Charging handle (on top, cylindrical knob)
        {
          const slide = addPart(parent, "cylinder", "mac10-charging-handle", [0.015, 0.03, 0.015], [0, 0.08, -0.05], [0, 0, 90], steel);
          // Base slot for the charging handle to slide in
          addPart(parent, "box", "mac10-charging-slot", [0.01, 0.01, 0.08], [0, 0.07, -0.05], [0, 0, 0], dark);
          setSlide(slide, 0, 0.08, -0.05);
        }
        
        // Rear sight (simple iron ring/plate)
        addPart(parent, "box", "mac10-rear-sight", [0.02, 0.025, 0.01], [0, 0.08, 0.02], [0, 0, 0], dark);
        addPart(parent, "cylinder", "mac10-rear-sight-hole", [0.01, 0.02, 0.01], [0, 0.08, 0.02], [90, 0, 0], dark); // simulated hole
        
        // Front sight (post with side guards)
        addPart(parent, "box", "mac10-front-sight-base", [0.015, 0.02, 0.015], [0, 0.08, -0.12], [0, 0, 0], dark);
        addPart(parent, "box", "mac10-front-sight-guard1", [0.005, 0.03, 0.015], [-0.015, 0.085, -0.12], [0, 0, 0], dark);
        addPart(parent, "box", "mac10-front-sight-guard2", [0.005, 0.03, 0.015], [0.015, 0.085, -0.12], [0, 0, 0], dark);

        // Magazine (straight, goes right into the grip)
        const macMag = new pc.Entity("mac10-mag-group");
        parent.addChild(macMag);
        macMag.setLocalPosition(0, 0, 0);
        addPart(macMag, "box", "mac10-mag", [0.03, 0.16, 0.045], [0, -0.14, -0.02], [0, 0, 0], dark);
        setMag(macMag, 0, 0, 0);

        // Grip (which is also the magwell)
        addPart(parent, "box", "mac10-grip", [0.04, 0.1, 0.055], [0, -0.08, -0.02], [0, 0, 0], polyMat);
        
        // Trigger and guard
        addPart(parent, "box", "mac10-trigger-guard", [0.01, 0.01, 0.05], [0, -0.04, -0.06], [0, 0, 0], dark);
        addPart(parent, "box", "mac10-trigger", [0.008, 0.02, 0.01], [0, -0.02, -0.05], [0, 0, 0], steel);

        // Wire stock (collapsed / tiny at the back)
        addPart(parent, "cylinder", "mac10-stock-rod1", [0.006, 0.12, 0.006], [-0.02, -0.02, 0.08], [90, 0, 0], steel);
        addPart(parent, "cylinder", "mac10-stock-rod2", [0.006, 0.12, 0.006], [0.02, -0.02, 0.08], [90, 0, 0], steel);
        addPart(parent, "box", "mac10-stock-pad", [0.05, 0.03, 0.01], [0, -0.02, 0.14], [0, 0, 0], steel);

      } else if (weaponId === "mp9") {
        // MP9 - B&T MP9 (compact, integrated foregrip, small sight on top, folding stock on side)
        const receiverMat = mat(0.12, 0.12, 0.12, 0.4); // Very dark gray/black polymer
        const darkPoly = mat(0.1, 0.1, 0.1, 0.2);
        
        // Main blocky receiver (lower and upper combined)
        addPart(parent, "box", "mp9-receiver", [0.04, 0.08, 0.20], [0, -0.02, -0.05], [0, 0, 0], receiverMat);
        // Top rail
        addPart(parent, "box", "mp9-rail-top", [0.03, 0.015, 0.18], [0, 0.025, -0.05], [0, 0, 0], dark);
        
        // Barrel (short, barely extending)
        addPart(parent, "cylinder", "mp9-barrel", [0.015, 0.06, 0.015], [0, -0.01, -0.17], [90, 0, 0], steel);
        // Muzzle device / suppressor lug
        addPart(parent, "cylinder", "mp9-muzzle-device", [0.02, 0.03, 0.02], [0, -0.01, -0.19], [90, 0, 0], dark);
        
        // Charging handle (rear, M4 style but smaller)
        {
          const slide = addPart(parent, "box", "mp9-charging-handle", [0.03, 0.015, 0.03], [0, 0.03, 0.06], [0, 0, 0], darkPoly);
          setSlide(slide, 0, 0.03, 0.06);
        }
        
        // Red dot sight (Micro style)
        addPart(parent, "box", "mp9-sight-mount", [0.025, 0.02, 0.03], [0, 0.04, -0.02], [0, 0, 0], dark);
        addPart(parent, "cylinder", "mp9-sight-tube", [0.025, 0.04, 0.025], [0, 0.06, -0.02], [90, 0, 0], dark);
        addPart(parent, "cylinder", "mp9-sight-lens", [0.022, 0.042, 0.022], [0, 0.06, -0.02], [90, 0, 0], mat(0.8, 0.2, 0.2, 0.9));
        
        // Rear sight (simple iron ring/plate just behind the red dot)
        addPart(parent, "box", "mp9-rear-sight", [0.02, 0.02, 0.01], [0, 0.04, 0.04], [0, 0, 0], dark);

        // Magazine (straight, goes right into the grip)
        const mp9Mag = new pc.Entity("mp9-mag-group");
        parent.addChild(mp9Mag);
        mp9Mag.setLocalPosition(0, 0, 0);
        addPart(mp9Mag, "box", "mp9-mag", [0.025, 0.18, 0.035], [0, -0.16, 0.02], [5, 0, 0], poly);
        // Base plate
        addPart(mp9Mag, "box", "mp9-mag-base", [0.03, 0.01, 0.04], [0, -0.25, 0.03], [5, 0, 0], darkPoly);
        setMag(mp9Mag, 0, 0, 0);

        // Grip (which is also the magwell)
        addPart(parent, "box", "mp9-grip", [0.035, 0.11, 0.045], [0, -0.09, 0.01], [5, 0, 0], darkPoly);
        
        // Integrated front vertical grip (angled forward slightly)
        addPart(parent, "box", "mp9-front-grip-mount", [0.03, 0.03, 0.08], [0, -0.06, -0.12], [0, 0, 0], receiverMat);
        addPart(parent, "cylinder", "mp9-front-grip", [0.025, 0.1, 0.03], [0, -0.11, -0.10], [10, 0, 0], darkPoly);
        
        // Trigger and guard
        addPart(parent, "box", "mp9-trigger-guard", [0.01, 0.01, 0.05], [0, -0.04, -0.03], [0, 0, 0], darkPoly);
        addPart(parent, "box", "mp9-trigger", [0.008, 0.02, 0.01], [0, -0.02, -0.02], [0, 0, 0], steel);

        // Folded stock (folded to the right side of the receiver)
        addPart(parent, "box", "mp9-stock-hinge", [0.015, 0.04, 0.02], [0.02, -0.02, 0.06], [0, 0, 0], dark);
        addPart(parent, "box", "mp9-stock-arm", [0.01, 0.025, 0.18], [0.025, -0.02, -0.04], [0, 0, 0], darkPoly);
        addPart(parent, "box", "mp9-stock-butt", [0.01, 0.06, 0.02], [0.025, -0.02, -0.12], [0, 0, 0], darkPoly);

      } else {
        // Generic SMG
        // Receiver
        addPart(parent, "box", "smg-receiver", [0.06, 0.08, 0.30], [0, 0, -0.1], [0, 0, 0], dark);
        addPart(parent, "box", "smg-rail", [0.04, 0.02, 0.25], [0, 0.05, -0.1], [0, 0, 0], steel);
        // Red Dot Sight
        addPart(parent, "box", "smg-sight-base", [0.03, 0.02, 0.06], [0, 0.07, -0.05], [0, 0, 0], dark);
        addPart(parent, "cylinder", "smg-sight-tube", [0.04, 0.06, 0.04], [0, 0.10, -0.05], [90, 0, 0], dark);
        addPart(parent, "cylinder", "smg-sight-lens", [0.035, 0.062, 0.035], [0, 0.10, -0.05], [90, 0, 0], mat(0.8, 0.2, 0.2, 0.9));
        // Barrel
        addPart(parent, "cylinder", "smg-barrel", [0.025, 0.15, 0.025], [0, 0.01, -0.30], [90, 0, 0], steel);
        addPart(parent, "cylinder", "smg-suppressor", [0.04, 0.12, 0.04], [0, 0.01, -0.40], [90, 0, 0], dark);
        // Magazine
        addPart(parent, "box", "smg-mag", [0.035, 0.16, 0.05], [0, -0.12, -0.02], [10, 0, 0], poly);
        addPart(parent, "box", "smg-mag-rib1", [0.037, 0.14, 0.01], [0, -0.12, -0.01], [10, 0, 0], dark);
        addPart(parent, "box", "smg-mag-rib2", [0.037, 0.14, 0.01], [0, -0.12, -0.03], [10, 0, 0], dark);
        // Grip / foregrip
        addPart(parent, "box", "smg-grip", [0.04, 0.1, 0.04], [0, -0.08, 0.1], [10, 0, 0], poly);
        addPart(parent, "cylinder", "smg-foregrip", [0.03, 0.08, 0.03], [0, -0.06, -0.20], [0, 0, 0], poly);
        addPart(parent, "cylinder", "smg-charging-handle", [0.01, 0.04, 0.01], [0.04, 0.02, -0.15], [0, 0, 90], steel);
        // Stock
        addPart(parent, "cylinder", "smg-stock-rod1", [0.01, 0.15, 0.01], [-0.02, 0, 0.12], [90, 0, 0], steel);
        addPart(parent, "cylinder", "smg-stock-rod2", [0.01, 0.15, 0.01], [0.02, 0, 0.12], [90, 0, 0], steel);
        addPart(parent, "box", "smg-stock-pad", [0.06, 0.08, 0.02], [0, 0, 0.2], [0, 0, 0], poly);
        addPart(parent, "box", "smg-accent", [0.065, 0.01, 0.15], [0, 0.02, -0.15], [0, 0, 0], accent);
      }
      break;

    case "rifle":
      if (weaponId === "ak47") {
        const wood = mat(0.4, 0.2, 0.05, 0.1);
        addPart(parent, "box", "ak-receiver", [0.045, 0.07, 0.25], [0, 0, 0.05], [0, 0, 0], steel);
        addPart(parent, "cylinder", "ak-dust-cover", [0.045, 0.25, 0.045], [0, 0.035, 0.05], [90, 0, 0], dark);
        {
          const slide = addPart(parent, "cylinder", "ak-charging-handle", [0.012, 0.04, 0.012], [0.03, 0.02, 0.02], [0, 0, 90], steel);
          setSlide(slide, 0.03, 0.02, 0.02);
        }
        addPart(parent, "cylinder", "ak-barrel", [0.02, 0.40, 0.02], [0, 0.01, -0.25], [90, 0, 0], dark);
        addPart(parent, "cylinder", "ak-gas-tube", [0.015, 0.15, 0.015], [0, 0.04, -0.15], [90, 0, 0], dark);
        addPart(parent, "box", "ak-gas-block", [0.02, 0.04, 0.03], [0, 0.025, -0.23], [10, 0, 0], steel);
        addPart(parent, "box", "ak-sight-front-base", [0.015, 0.04, 0.02], [0, 0.03, -0.42], [0, 0, 0], steel);
        addPart(parent, "cylinder", "ak-sight-front-post", [0.005, 0.02, 0.005], [0, 0.05, -0.42], [0, 0, 0], dark);
        addPart(parent, "cylinder", "ak-sight-front-ring", [0.015, 0.01, 0.015], [0, 0.05, -0.42], [90, 0, 0], steel);
        addPart(parent, "box", "ak-sight-rear-base", [0.025, 0.02, 0.04], [0, 0.045, -0.05], [0, 0, 0], steel);
        addPart(parent, "box", "ak-sight-rear-leaf", [0.02, 0.01, 0.06], [0, 0.055, -0.06], [-5, 0, 0], dark);
        addPart(parent, "cylinder", "ak-muzzle", [0.022, 0.04, 0.022], [0, 0.01, -0.46], [90, 0, 0], steel);
        addPart(parent, "box", "ak-muzzle-slant", [0.025, 0.025, 0.02], [0, 0.02, -0.47], [-30, 0, 0], steel);
        addPart(parent, "box", "ak-handguard-lower", [0.05, 0.04, 0.16], [0, 0.0, -0.15], [0, 0, 0], wood);
        addPart(parent, "cylinder", "ak-handguard-upper", [0.04, 0.12, 0.04], [0, 0.04, -0.15], [90, 0, 0], wood);
        // Magazine (group)
        const akMag = new pc.Entity("ak-mag-group");
        parent.addChild(akMag);
        akMag.setLocalPosition(0, 0, 0);
        addPart(akMag, "box", "ak-mag-top", [0.03, 0.08, 0.07], [0, -0.07, -0.02], [10, 0, 0], steel);
        addPart(akMag, "box", "ak-mag-mid", [0.03, 0.08, 0.07], [0, -0.13, 0.0], [25, 0, 0], steel);
        addPart(akMag, "box", "ak-mag-bottom", [0.03, 0.06, 0.07], [0, -0.18, 0.03], [40, 0, 0], steel);
        addPart(akMag, "box", "ak-mag-rib1", [0.032, 0.18, 0.005], [0, -0.12, 0.01], [25, 0, 0], dark);
        addPart(akMag, "box", "ak-mag-rib2", [0.032, 0.18, 0.005], [0, -0.11, -0.01], [20, 0, 0], dark);
        setMag(akMag, 0, 0, 0);
        // Grip + trigger + stock
        addPart(parent, "box", "ak-grip", [0.035, 0.1, 0.045], [0, -0.08, 0.14], [15, 0, 0], wood);
        addPart(parent, "box", "ak-guard-bottom", [0.01, 0.01, 0.05], [0, -0.04, 0.08], [0, 0, 0], steel);
        addPart(parent, "box", "ak-guard-front", [0.01, 0.04, 0.01], [0, -0.02, 0.06], [0, 0, 0], steel);
        addPart(parent, "box", "ak-trigger", [0.008, 0.025, 0.01], [0, -0.01, 0.09], [15, 0, 0], steel);
        addPart(parent, "box", "ak-stock-mount", [0.04, 0.06, 0.04], [0, 0, 0.18], [0, 0, 0], steel);
        addPart(parent, "box", "ak-stock-body", [0.045, 0.08, 0.22], [0, -0.02, 0.28], [-5, 0, 0], wood);
        addPart(parent, "box", "ak-stock-pad", [0.045, 0.09, 0.01], [0, -0.03, 0.39], [-5, 0, 0], steel);
      } else if (weaponId === "m4a4" || weaponId === "m4a1_s") {
        const receiverMat = mat(0.25, 0.25, 0.26, 0.5);
        const polyMat = mat(0.15, 0.15, 0.15, 0.2);
        const railMat = mat(0.2, 0.2, 0.2, 0.4);
        // Lower receiver
        addPart(parent, "box", "m4-lower", [0.045, 0.07, 0.18], [0, -0.01, 0.05], [0, 0, 0], receiverMat);
        addPart(parent, "box", "m4-magwell", [0.05, 0.06, 0.07], [0, -0.05, -0.01], [5, 0, 0], receiverMat);
        addPart(parent, "box", "m4-trigger-guard", [0.01, 0.01, 0.05], [0, -0.06, 0.05], [0, 0, 0], receiverMat);
        addPart(parent, "box", "m4-trigger", [0.008, 0.03, 0.01], [0, -0.03, 0.06], [15, 0, 0], steel);
        addPart(parent, "box", "m4-mag-release", [0.01, 0.015, 0.015], [0.026, -0.03, 0.01], [0, 0, 0], steel);
        addPart(parent, "box", "m4-selector", [0.005, 0.02, 0.005], [-0.025, 0.01, 0.08], [0, 0, 45], steel);
        addPart(parent, "box", "m4-bolt-catch", [0.01, 0.02, 0.015], [-0.025, 0.01, 0.02], [0, 0, 0], steel);
        // Upper receiver
        addPart(parent, "box", "m4-upper", [0.05, 0.055, 0.22], [0, 0.05, 0.04], [0, 0, 0], receiverMat);
        addPart(parent, "box", "m4-top-rail", [0.052, 0.01, 0.22], [0, 0.08, 0.04], [0, 0, 0], railMat);
        addPart(parent, "box", "m4-ejection-port", [0.055, 0.02, 0.06], [0.005, 0.05, 0.02], [0, 0, 0], dark);
        addPart(parent, "box", "m4-forward-assist", [0.015, 0.02, 0.03], [0.025, 0.05, 0.08], [0, 15, 0], receiverMat);
        addPart(parent, "box", "m4-brass-deflector", [0.02, 0.03, 0.02], [0.025, 0.05, 0.06], [0, 45, 0], receiverMat);
        // Bolt as slide
        {
          const slide = addPart(parent, "box", "m4-bolt", [0.04, 0.015, 0.05], [0.005, 0.05, 0.02], [0, 0, 0], steel);
          setSlide(slide, 0.005, 0.05, 0.02);
        }
        // Barrel + gas + muzzle
        addPart(parent, "cylinder", "m4-barrel", [0.022, 0.35, 0.022], [0, 0.04, -0.25], [90, 0, 0], steel);
        addPart(parent, "box", "m4-gas-block", [0.03, 0.05, 0.04], [0, 0.05, -0.32], [0, 0, 0], receiverMat);
        addPart(parent, "cylinder", "m4-muzzle", [0.025, 0.05, 0.025], [0, 0.04, -0.44], [90, 0, 0], dark);
        addPart(parent, "cylinder", "m4-muzzle-ring1", [0.028, 0.005, 0.028], [0, 0.04, -0.42], [90, 0, 0], steel);
        addPart(parent, "cylinder", "m4-muzzle-ring2", [0.028, 0.005, 0.028], [0, 0.04, -0.46], [90, 0, 0], steel);
        if (weaponId === "m4a1_s") {
          addPart(parent, "cylinder", "m4-suppressor", [0.035, 0.20, 0.035], [0, 0.04, -0.55], [90, 0, 0], dark);
        }
        // Handguard + rails
        addPart(parent, "box", "m4-handguard", [0.048, 0.048, 0.198], [0, 0.04, -0.17], [0, 0, 0], polyMat);
        addPart(parent, "box", "m4-hg-rail-top", [0.052, 0.01, 0.20], [0, 0.07, -0.17], [0, 0, 0], railMat);
        addPart(parent, "box", "m4-hg-rail-bot", [0.052, 0.01, 0.20], [0, 0.01, -0.17], [0, 0, 0], railMat);
        addPart(parent, "box", "m4-hg-rail-l", [0.01, 0.052, 0.20], [-0.03, 0.04, -0.17], [0, 0, 0], railMat);
        addPart(parent, "box", "m4-hg-rail-r", [0.01, 0.052, 0.20], [0.03, 0.04, -0.17], [0, 0, 0], railMat);
        addPart(parent, "box", "m4-rail-cover-l", [0.012, 0.04, 0.15], [-0.032, 0.04, -0.17], [0, 0, 0], polyMat);
        addPart(parent, "box", "m4-rail-cover-r", [0.012, 0.04, 0.15], [0.032, 0.04, -0.17], [0, 0, 0], polyMat);
        addPart(parent, "cylinder", "m4-foregrip", [0.025, 0.08, 0.025], [0, -0.02, -0.15], [0, 0, 0], polyMat);
        // STANAG mag (group)
        const m4Mag = new pc.Entity("m4-mag-group");
        parent.addChild(m4Mag);
        m4Mag.setLocalPosition(0, 0, 0);
        addPart(m4Mag, "box", "m4-mag-top", [0.032, 0.08, 0.065], [0, -0.08, -0.01], [5, 0, 0], steel);
        addPart(m4Mag, "box", "m4-mag-bot", [0.032, 0.08, 0.065], [0, -0.15, 0.0], [10, 0, 0], steel);
        addPart(m4Mag, "box", "m4-mag-rib1", [0.034, 0.15, 0.01], [0, -0.11, -0.02], [8, 0, 0], dark);
        addPart(m4Mag, "box", "m4-mag-rib2", [0.034, 0.15, 0.01], [0, -0.11, 0.01], [8, 0, 0], dark);
        setMag(m4Mag, 0, 0, 0);
        // Grip + stock
        addPart(parent, "box", "m4-grip", [0.035, 0.1, 0.05], [0, -0.08, 0.10], [20, 0, 0], polyMat);
        addPart(parent, "box", "m4-grip-bump", [0.035, 0.02, 0.02], [0, -0.06, 0.07], [20, 0, 0], polyMat);
        addPart(parent, "cylinder", "m4-buffer-tube", [0.03, 0.18, 0.03], [0, 0.03, 0.22], [90, 0, 0], steel);
        addPart(parent, "box", "m4-stock-body", [0.05, 0.08, 0.15], [0, 0.01, 0.26], [0, 0, 0], polyMat);
        addPart(parent, "box", "m4-stock-cheek", [0.06, 0.03, 0.14], [0, 0.04, 0.26], [0, 0, 0], polyMat);
        addPart(parent, "box", "m4-stock-pad", [0.05, 0.10, 0.02], [0, 0.0, 0.34], [-5, 0, 0], dark);
        // Iron sights
        addPart(parent, "box", "m4-rear-sight-base", [0.035, 0.03, 0.04], [0, 0.09, 0.12], [0, 0, 0], receiverMat);
        addPart(parent, "box", "m4-rear-sight-post", [0.01, 0.04, 0.01], [0, 0.11, 0.12], [0, 0, 0], receiverMat);
        addPart(parent, "cylinder", "m4-rear-sight-knob", [0.015, 0.045, 0.015], [0, 0.09, 0.12], [0, 0, 90], dark);
        addPart(parent, "box", "m4-front-sight-base", [0.02, 0.04, 0.02], [0, 0.08, -0.32], [0, 0, 0], receiverMat);
        addPart(parent, "box", "m4-front-sight-post", [0.005, 0.02, 0.005], [0, 0.10, -0.32], [0, 0, 0], steel);
        addPart(parent, "box", "m4-front-sight-guard-l", [0.005, 0.03, 0.01], [-0.01, 0.10, -0.32], [0, 0, 15], receiverMat);
        addPart(parent, "box", "m4-front-sight-guard-r", [0.005, 0.03, 0.01], [0.01, 0.10, -0.32], [0, 0, -15], receiverMat);
      } else {
        // Fallback generic rifle
        addPart(parent, "box", "rifle-lower", [0.05, 0.08, 0.20], [0, -0.02, 0.05], [0, 0, 0], dark);
        addPart(parent, "box", "rifle-upper", [0.055, 0.06, 0.22], [0, 0.05, 0.04], [0, 0, 0], steel);
        {
          const slide = addPart(parent, "box", "rifle-charging-handle", [0.01, 0.02, 0.04], [0.03, 0.05, 0.02], [0, 0, 0], steel);
          setSlide(slide, 0.03, 0.05, 0.02);
        }
        addPart(parent, "cylinder", "rifle-barrel", [0.025, 0.35, 0.025], [0, 0.04, -0.25], [90, 0, 0], steel);
        addPart(parent, "box", "rifle-handguard", [0.06, 0.07, 0.24], [0, 0.04, -0.18], [0, 0, 0], poly);
        const rMag = new pc.Entity("rifle-mag-group");
        parent.addChild(rMag);
        rMag.setLocalPosition(0, 0, 0);
        addPart(rMag, "box", "rifle-mag-top", [0.035, 0.08, 0.06], [0, -0.08, -0.02], [5, 0, 0], poly);
        addPart(rMag, "box", "rifle-mag-bottom", [0.035, 0.08, 0.06], [0, -0.15, -0.01], [15, 0, 0], poly);
        setMag(rMag, 0, 0, 0);
        addPart(parent, "box", "rifle-grip", [0.04, 0.1, 0.05], [0, -0.09, 0.12], [15, 0, 0], poly);
        addPart(parent, "box", "rifle-stock-body", [0.05, 0.1, 0.12], [0, -0.01, 0.25], [0, 0, 0], poly);
      }
      break;

    case "sniper":
      const awpGreen = mat(0.18, 0.28, 0.18, 0.2); // Olive green
      addPart(parent, "box", "awp-chassis", [0.06, 0.08, 0.45], [0, 0, -0.05], [0, 0, 0], awpGreen);
      addPart(parent, "cylinder", "awp-barrel", [0.035, 0.45, 0.035], [0, 0.03, -0.4], [90, 0, 0], dark);
      addPart(parent, "cylinder", "awp-flute1", [0.005, 0.35, 0.005], [0.015, 0.045, -0.4], [90, 0, 0], steel);
      addPart(parent, "cylinder", "awp-flute2", [0.005, 0.35, 0.005], [-0.015, 0.045, -0.4], [90, 0, 0], steel);
      addPart(parent, "cylinder", "awp-flute3", [0.005, 0.35, 0.005], [0.015, 0.015, -0.4], [90, 0, 0], steel);
      addPart(parent, "cylinder", "awp-flute4", [0.005, 0.35, 0.005], [-0.015, 0.015, -0.4], [90, 0, 0], steel);
      addPart(parent, "box", "awp-muzzle", [0.05, 0.05, 0.06], [0, 0.03, -0.65], [0, 0, 0], dark);
      addPart(parent, "box", "awp-muzzle-hole", [0.052, 0.02, 0.02], [0, 0.03, -0.65], [0, 0, 0], steel);
      addPart(parent, "box", "awp-scope-mount", [0.03, 0.03, 0.12], [0, 0.05, 0.05], [0, 0, 0], steel);
      addPart(parent, "cylinder", "awp-scope-tube", [0.04, 0.25, 0.04], [0, 0.09, 0.05], [90, 0, 0], dark);
      addPart(parent, "cylinder", "awp-turret-top", [0.02, 0.02, 0.02], [0, 0.12, 0.05], [0, 0, 0], steel);
      addPart(parent, "cylinder", "awp-turret-side", [0.02, 0.02, 0.02], [0.03, 0.09, 0.05], [0, 0, 90], steel);
      addPart(parent, "cylinder", "awp-scope-front", [0.055, 0.08, 0.055], [0, 0.09, -0.08], [90, 0, 0], dark);
      addPart(parent, "cylinder", "awp-scope-rear", [0.045, 0.05, 0.045], [0, 0.09, 0.18], [90, 0, 0], dark);

      // Bolt group for slide animation
      const awpBolt = new pc.Entity("awp-bolt-group");
      parent.addChild(awpBolt);
      awpBolt.setLocalPosition(0, 0.02, 0.1);
      addPart(awpBolt, "cylinder", "awp-bolt-stem", [0.015, 0.05, 0.015], [0.04, 0, 0], [0, 0, 90], steel);
      addPart(awpBolt, "sphere", "awp-bolt-knob", [0.03, 0.03, 0.03], [0.07, 0, 0], [0, 0, 0], dark);
      setSlide(awpBolt, 0, 0.02, 0.1);

      // Mag group for reload animation
      const awpMagGroup = new pc.Entity("awp-mag-group");
      parent.addChild(awpMagGroup);
      awpMagGroup.setLocalPosition(0, 0, 0);
      addPart(awpMagGroup, "box", "awp-mag", [0.04, 0.1, 0.08], [0, -0.08, 0.05], [0, 0, 0], dark);
      setMag(awpMagGroup, 0, 0, 0);

      addPart(parent, "box", "awp-grip", [0.04, 0.1, 0.05], [0, -0.08, 0.18], [15, 0, 0], dark);
      addPart(parent, "box", "awp-stock", [0.05, 0.12, 0.15], [0, 0, 0.25], [0, 0, 0], awpGreen);
      addPart(parent, "box", "awp-cheek-rest", [0.04, 0.02, 0.1], [0, 0.07, 0.25], [0, 0, 0], dark);
      addPart(parent, "cylinder", "awp-cheek-knob1", [0.01, 0.01, 0.01], [0.025, 0.06, 0.22], [0, 0, 90], steel);
      addPart(parent, "cylinder", "awp-cheek-knob2", [0.01, 0.01, 0.01], [0.025, 0.06, 0.28], [0, 0, 90], steel);
      addPart(parent, "cylinder", "awp-monopod", [0.01, 0.06, 0.01], [0, -0.08, 0.30], [0, 0, 0], steel);
      addPart(parent, "box", "awp-bipod-mount", [0.04, 0.02, 0.04], [0, -0.04, -0.2], [0, 0, 0], dark);
      addPart(parent, "cylinder", "awp-bipod-l", [0.015, 0.15, 0.015], [-0.03, -0.02, -0.2], [90, 0, -10], dark);
      addPart(parent, "cylinder", "awp-bipod-r", [0.015, 0.15, 0.015], [0.03, -0.02, -0.2], [90, 0, 10], dark);
      addPart(parent, "cylinder", "awp-bipod-spring-l", [0.005, 0.05, 0.005], [-0.02, -0.04, -0.15], [90, 0, -10], steel);
      addPart(parent, "cylinder", "awp-bipod-spring-r", [0.005, 0.05, 0.005], [0.02, -0.04, -0.15], [90, 0, 10], steel);
      break;

    case "grenade":
      if (weaponId === "smoke") {
        const smokeBody = mat(0.6, 0.6, 0.6, 0.4);
        const smokeStripe = mat(0.2, 0.8, 0.2, 0.2);
        const smokeTop = mat(0.3, 0.3, 0.3, 0.5);
        addPart(parent, "cylinder", "smoke-body", [0.06, 0.12, 0.06], [0, -0.02, 0], [0, 0, 0], smokeBody);
        addPart(parent, "cylinder", "smoke-stripe", [0.062, 0.02, 0.062], [0, 0.02, 0], [0, 0, 0], smokeStripe);
        addPart(parent, "cylinder", "smoke-cap", [0.06, 0.01, 0.06], [0, 0.045, 0], [0, 0, 0], smokeTop);
        addPart(parent, "cylinder", "smoke-neck", [0.02, 0.02, 0.02], [0, 0.06, 0], [0, 0, 0], steel);
        addPart(parent, "box", "smoke-spoon", [0.015, 0.08, 0.015], [0.02, 0.04, 0], [0, 0, 10], steel);
        addPart(parent, "cylinder", "smoke-pin-ring", [0.02, 0.005, 0.02], [-0.02, 0.07, 0], [90, 0, 0], steel);
        addPart(parent, "box", "smoke-vent1", [0.01, 0.01, 0.065], [0, 0.04, 0], [0, 45, 0], dark);
        addPart(parent, "box", "smoke-vent2", [0.01, 0.01, 0.065], [0, 0.04, 0], [0, -45, 0], dark);
      } else if (weaponId === "flash") {
        const flashBody = mat(0.7, 0.7, 0.75, 0.8);
        const flashStripe = mat(0.2, 0.4, 0.9, 0.3);
        addPart(parent, "cylinder", "flash-body", [0.05, 0.11, 0.05], [0, -0.02, 0], [0, 0, 0], flashBody);
        addPart(parent, "cylinder", "flash-stripe", [0.052, 0.015, 0.052], [0, 0.01, 0], [0, 0, 0], flashStripe);
        addPart(parent, "cylinder", "flash-neck", [0.02, 0.015, 0.02], [0, 0.04, 0], [0, 0, 0], steel);
        addPart(parent, "box", "flash-spoon", [0.012, 0.07, 0.012], [0.015, 0.02, 0], [0, 0, 10], steel);
        addPart(parent, "cylinder", "flash-pin-ring", [0.015, 0.005, 0.015], [-0.015, 0.05, 0], [90, 0, 0], steel);
        addPart(parent, "cylinder", "flash-indent1", [0.051, 0.005, 0.051], [0, -0.05, 0], [0, 0, 0], dark);
        addPart(parent, "cylinder", "flash-indent2", [0.051, 0.005, 0.051], [0, -0.01, 0], [0, 0, 0], dark);
      } else if (weaponId === "molotov") {
        // Molotov — CS:GO-style translucent green bottle with visible
        // fuel and a lit rag wick. Geometry lives in player/molotovDesign.ts
        // so the in-hand model and the in-flight model (grenadeViewModel)
        // stay identical.
        buildMolotov(parent, addPart, mat);
      } else if (weaponId === "incendiary") {
        const canShell = mat(0.38, 0.44, 0.48, 0.6);
        const canPaint = mat(0.52, 0.58, 0.62, 0.55);
        const canDark = mat(0.14, 0.16, 0.18, 0.4);
        const hazardRed = mat(0.82, 0.12, 0.06, 0.35);
        const hazardOrange = mat(0.92, 0.42, 0.06, 0.3);
        const stencil = mat(0.9, 0.92, 0.88, 0.28);
        const nozzle = mat(0.22, 0.24, 0.26, 0.5);
        addPart(parent, "cylinder", "incendiary-body", [0.058, 0.11, 0.058], [0, -0.028, 0], [0, 0, 0], canShell);
        addPart(parent, "cylinder", "incendiary-body-rim-top", [0.06, 0.008, 0.06], [0, 0.028, 0], [0, 0, 0], canDark);
        addPart(parent, "cylinder", "incendiary-body-rim-bot", [0.06, 0.008, 0.06], [0, -0.086, 0], [0, 0, 0], canDark);
        addPart(parent, "cylinder", "incendiary-paint-panel", [0.056, 0.075, 0.056], [0, -0.02, 0], [0, 0, 0], canPaint);
        addPart(parent, "cylinder", "incendiary-band-upper", [0.059, 0.012, 0.059], [0, 0.012, 0], [0, 0, 0], hazardRed);
        addPart(parent, "cylinder", "incendiary-band-mid", [0.059, 0.01, 0.059], [0, -0.045, 0], [0, 0, 0], hazardOrange);
        addPart(parent, "cylinder", "incendiary-band-lower", [0.059, 0.012, 0.059], [0, -0.078, 0], [0, 0, 0], hazardRed);
        addPart(parent, "box", "incendiary-label-plate", [0.048, 0.055, 0.004], [0, -0.022, -0.058], [0, 0, 0], stencil);
        addPart(parent, "box", "incendiary-flame-stem", [0.01, 0.028, 0.004], [0, -0.024, -0.061], [0, 0, 0], hazardOrange);
        addPart(parent, "sphere", "incendiary-flame-head", [0.016, 0.018, 0.004], [0.005, -0.008, -0.062], [0, 0, 0], hazardRed);
        addPart(parent, "box", "incendiary-text-i", [0.008, 0.022, 0.004], [-0.014, -0.038, -0.061], [0, 0, 0], canDark);
        addPart(parent, "box", "incendiary-text-nc", [0.02, 0.012, 0.004], [0.016, -0.042, -0.061], [0, 0, 0], canDark);
        addPart(parent, "box", "incendiary-rib-l", [0.005, 0.09, 0.012], [-0.058, -0.025, 0], [0, 0, 0], canDark);
        addPart(parent, "box", "incendiary-rib-r", [0.005, 0.09, 0.012], [0.058, -0.025, 0], [0, 0, 0], canDark);
        addPart(parent, "cylinder", "incendiary-nozzle", [0.04, 0.02, 0.04], [0, 0.048, 0], [0, 0, 0], nozzle);
        addPart(parent, "cylinder", "incendiary-fuse-post", [0.018, 0.028, 0.018], [0, 0.072, 0], [0, 0, 0], steel);
        addPart(parent, "box", "incendiary-spoon", [0.012, 0.075, 0.012], [0.022, 0.048, 0], [0, 0, 14], steel);
        addPart(parent, "cylinder", "incendiary-pin-ring", [0.02, 0.005, 0.02], [-0.022, 0.086, 0], [90, 0, 0], steel);
        addPart(parent, "box", "incendiary-lever-tab", [0.032, 0.01, 0.022], [0, 0.09, -0.018], [0, 0, 0], hazardRed);
      } else {
        // Default HE Grenade
        const heBody = mat(0.22, 0.32, 0.12, 0.2);
        addPart(parent, "sphere", "he-body", [0.07, 0.09, 0.07], [0, -0.02, 0], [0, 0, 0], heBody);
        for (let i = 0; i < 4; i++) {
          addPart(parent, "box", `he-rib-v${i}`, [0.075, 0.08, 0.01], [0, -0.02, 0], [0, i * 45, 0], dark);
        }
        addPart(parent, "box", "he-rib-h1", [0.075, 0.01, 0.075], [0, -0.04, 0], [0, 0, 0], dark);
        addPart(parent, "box", "he-rib-h2", [0.075, 0.01, 0.075], [0, 0, 0], [0, 0, 0], dark);
        addPart(parent, "cylinder", "he-neck", [0.025, 0.02, 0.025], [0, 0.035, 0], [0, 0, 0], steel);
        addPart(parent, "box", "he-spoon", [0.015, 0.08, 0.015], [0.02, 0.01, 0], [0, 0, 10], steel);
        addPart(parent, "cylinder", "he-pin-ring", [0.02, 0.005, 0.02], [-0.02, 0.045, 0], [90, 0, 0], steel);
        addPart(parent, "cylinder", "he-stripe", [0.026, 0.005, 0.026], [0, 0.04, 0], [0, 0, 0], mat(0.8, 0.8, 0.1, 0.2));
      }
      break;

    default:
      addPart(parent, "box", "fallback", [0.06, 0.07, 0.16], [0, 0, -0.06], [0, 0, 0], dark);
  }
}
